import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, LatLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Text, Title, Paper, Button } from '@mantine/core';
import { IconNavigation } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import { FullSchool } from '../../types/Localities';
import { schoolsGeocoding } from '../../types/SchoolGeocoding';
import { createPortal } from 'react-dom';
import { useTheme as useThemeContext } from '../../contexts/ThemeContext';
import { getMapTiles } from '../../utils/mapTileUtils';
import { debugLog, debugVerboseLog } from '../../utils/debugLog';

// Fix for default markers in react-leaflet
try {
    delete (Icon.Default.prototype as any)._getIconUrl;
    Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
} catch (error) {
    console.warn('Failed to set up leaflet icons:', error);
}

interface SchoolMapProps {
    schools: FullSchool[];
    selectedSchool?: FullSchool | null;
    onSchoolSelect?: (school: FullSchool) => void;
    height?: string | number;
    showPopup?: boolean;
    darkMode?: boolean; // Optional dark mode flag
    tileStyle?: 'voyager' | 'darkMatter' | 'tonerLite' | 'positron' | 'openstreetmap'; // Map tile style
    initialZoom?: number; // Optional initial zoom level
    buildings?: any[]; // Optional buildings array
    responsibilityCenters?: any[]; // Optional responsibility centers array
}

// Helper function to get coordinates for a school or entity
const getSchoolCoordinates = (school: FullSchool | any): [number, number] => {
    // Check if school is valid
    if (!school || !school.Id) {
        console.warn('⚠️ getSchoolCoordinates: Invalid school object:', school);
        return [30.3322, -81.6557]; // Fallback coordinates
    }

    // First, check if the entity has direct Latitude/Longitude (from geocoding in detail views)
    if ((school as any).Latitude && (school as any).Longitude) {
        return [(school as any).Latitude, (school as any).Longitude];
    }

    // Then, try to find the school in our geocoding lookup
    const geocodingEntry = schoolsGeocoding.find(entry => entry.Id === school.Id);

    if (geocodingEntry?.geocode) {
        // Use the stored geocoding data
        return [geocodingEntry.geocode.lat, geocodingEntry.geocode.lng];
    }

    // Fallback to placeholder coordinates (Jacksonville, FL)
    return [30.3322, -81.6557];
};

// Helper function to generate directions URLs
const getDirectionsUrl = (school: FullSchool, coordinates: [number, number]): string => {
    // Check if school is valid
    if (!school || !school.Address || !school.City || !school.State) {
        console.warn('⚠️ getDirectionsUrl: Invalid school object or missing address info:', school);
        return 'https://www.google.com/maps';
    }

    const address = `${school.Address}, ${school.City}, ${school.State} ${school.Zip || ''}`;
    const encodedAddress = encodeURIComponent(address);
    const [lat, lng] = coordinates;

    // Google Maps directions URL
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${encodedAddress}`;
};

// Helper function to create popup content with directions button
const createPopupContent = (school: FullSchool, coordinates: [number, number], isUsingFallback: boolean): string => {
    const directionsUrl = getDirectionsUrl(school, coordinates);

    return `
        <div style="
            min-width: 250px; 
            max-width: 300px; 
            font-family: Arial, sans-serif; 
            padding: 8px;
            background: white;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        ">
            <h6 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #333; word-wrap: break-word;">${school.DisplayName}</h6>
            <p style="margin: 4px 0; color: #666; font-size: 14px; word-wrap: break-word;">${school.Address}</p>
            <p style="margin: 4px 0; color: #666; font-size: 14px;">${school.City}, ${school.State} ${school.Zip}</p>
            ${school.PhoneNumber ? `<p style="margin: 4px 0; color: #666; font-size: 14px;">📞 ${school.PhoneNumber}</p>` : ''}
            ${isUsingFallback ? '<p style="margin: 4px 0; color: #666; font-size: 12px; font-style: italic;">📍 Using approximate location</p>' : ''}
            <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e0e0e0;">
                <a href="${directionsUrl}" target="_blank" style="
                    display: inline-block;
                    width: 100%;
                    padding: 10px 16px;
                    background: #1976d2;
                    color: white !important;
                    text-decoration: none;
                    border-radius: 4px;
                    text-align: center;
                    font-size: 14px;
                    font-weight: 500;
                    border: none;
                    cursor: pointer;
                    box-sizing: border-box;
                " onmouseover="this.style.background='#1565c0'" onmouseout="this.style.background='#1976d2'">
                    🗺️ Get Directions
                </a>
            </div>
        </div>
    `;
};

// Component to fit map bounds to markers
const MapBounds: React.FC<{ schools: FullSchool[] }> = ({ schools }) => {
    const map = useMap();

    useEffect(() => {
        if (schools.length > 0) {
            try {
                const bounds = new LatLngBounds(
                    schools
                        .filter(school => school.Address && school.City && school.State)
                        .map(school => getSchoolCoordinates(school))
                );

                if (bounds.isValid()) {
                    // Add a delay and check if map is ready
                    setTimeout(() => {
                        try {
                            // Check if map is ready and has valid container
                            if (map && map.getContainer() && map.getContainer().offsetParent !== null) {
                                map.fitBounds(bounds, { padding: [20, 20] });
                            } else {
                                console.warn('Map not ready for fitBounds operation');
                            }
                        } catch (error) {
                            console.warn('Error fitting map bounds:', error);
                        }
                    }, 200);
                }
            } catch (error) {
                console.warn('Error creating map bounds:', error);
            }
        }
    }, [schools, map]);

    return null;
};

// Component to handle map clicks
const MapClickHandler: React.FC<{ onMapClick: () => void }> = ({ onMapClick }) => {
    const map = useMap();

    useEffect(() => {
        const handleMapClick = () => {
            onMapClick();
        };

        map.on('click', handleMapClick);

        return () => {
            map.off('click', handleMapClick);
        };
    }, [map, onMapClick]);

    return null;
};

export const SchoolMap: React.FC<SchoolMapProps> = ({
    schools,
    selectedSchool: _selectedSchool,
    onSchoolSelect: _onSchoolSelect,
    height = '500px',
    showPopup: _showPopup = true,
    darkMode: darkModeProp,
    tileStyle = 'voyager',
    initialZoom,
    buildings,
    responsibilityCenters
}) => {
    const themeContext = useThemeContext();
    const { colorScheme } = useMantineColorScheme();
    const isDarkMode = darkModeProp !== undefined ? darkModeProp : (themeContext?.isDarkMode || colorScheme === 'dark');
    const [mapKey, setMapKey] = useState(0);
    const [mapError, setMapError] = useState<string | null>(null);
    const [selectedSchoolForDirections, setSelectedSchoolForDirections] = useState<FullSchool | null>(null);
    const [openPopupSchool, setOpenPopupSchool] = useState<FullSchool | null>(null);

    // Get tile configuration and log for debugging
    const tileConfig = getMapTiles(isDarkMode, tileStyle);
    useEffect(() => {
        debugVerboseLog('🗺️ Map tile style changed:', { tileStyle, isDarkMode, url: tileConfig.url });
    }, [tileStyle, isDarkMode, tileConfig.url]);

    // Debug openPopupSchool changes
    useEffect(() => {
        debugVerboseLog('🗺️ openPopupSchool changed:', openPopupSchool?.DisplayName, openPopupSchool?.Id);
    }, [openPopupSchool]);

    // Filter schools that have address information
    const schoolsWithAddress = schools.filter(school =>
        school.Address && school.City && school.State
    );

    // Determine center and zoom based on markers
    // If there's only one marker, center on it with higher zoom
    // Otherwise use default center with lower zoom
    let mapCenter: [number, number] = [30.3322, -81.6557]; // Default center (Jacksonville, FL)
    let mapZoom = initialZoom ?? 10; // Default zoom or use provided initialZoom

    // Check if we have buildings or responsibility centers (for detail views)
    const hasBuildings = buildings && buildings.length > 0;
    const hasResponsibilityCenters = responsibilityCenters && responsibilityCenters.length > 0;
    const totalMarkers = schoolsWithAddress.length + (hasBuildings ? buildings!.length : 0) + (hasResponsibilityCenters ? responsibilityCenters!.length : 0);

    if (totalMarkers === 1) {
        // Single marker - center on it with higher zoom
        if (schoolsWithAddress.length === 1) {
            mapCenter = getSchoolCoordinates(schoolsWithAddress[0]);
            mapZoom = initialZoom ?? 16; // Higher zoom for single location
        } else if (hasBuildings && buildings!.length === 1) {
            const building = buildings![0];
            if (building.Latitude && building.Longitude) {
                mapCenter = [building.Latitude, building.Longitude];
                mapZoom = initialZoom ?? 16;
            }
        } else if (hasResponsibilityCenters && responsibilityCenters!.length === 1) {
            const rc = responsibilityCenters![0];
            if (rc.Latitude && rc.Longitude) {
                mapCenter = [rc.Latitude, rc.Longitude];
                mapZoom = initialZoom ?? 16;
            }
        }
    } else if (totalMarkers > 1) {
        // Multiple markers - use default zoom or fit bounds
        mapZoom = initialZoom ?? 10;
    }

    // Also check if we have a single school with coordinates (for detail views)
    // This handles the case where a single entity is passed with coordinates
    if (schoolsWithAddress.length === 1 && !hasBuildings && !hasResponsibilityCenters) {
        const school = schoolsWithAddress[0];
        // Use geocoding coordinates - getSchoolCoordinates will handle Latitude/Longitude if present
        mapCenter = getSchoolCoordinates(school);
        mapZoom = initialZoom ?? 16;
    }

    // Temporarily disabled map re-render to prevent popup closing
    // useEffect(() => {
    //     // Force map re-render when schools change
    //     setMapKey(prev => prev + 1);
    //     setMapError(null);
    // }, [schools]);


    if (schoolsWithAddress.length === 0) {
        return (
            <Paper p="lg" ta="center" h={height}>
                <Title order={5} c="dimmed">
                    No schools with address information available
                </Title>
                <Text size="sm" c="dimmed">
                    Add address information to schools to display them on the map
                </Text>
            </Paper>
        );
    }

    if (mapError) {
        return (
            <Paper p="lg" ta="center" h={height}>
                <Title order={5} c="red">
                    Map Error
                </Title>
                <Text size="sm" c="dimmed">
                    {mapError}
                </Text>
            </Paper>
        );
    }

    return (
        <Box h={height} w="100%" pos="relative">
            {/* Fallback directions button */}
            {selectedSchoolForDirections && (
                <Box style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    padding: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    <Button
                        size="xs"
                        leftSection={<IconNavigation size={14} />}
                        onClick={() => {
                            const coordinates = getSchoolCoordinates(selectedSchoolForDirections);
                            const directionsUrl = getDirectionsUrl(selectedSchoolForDirections, coordinates);
                            window.open(directionsUrl, '_blank');
                        }}
                    >
                        Directions to {selectedSchoolForDirections.DisplayName}
                    </Button>
                    <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => setSelectedSchoolForDirections(null)}
                        style={{ minWidth: 'auto', padding: '2px 6px' }}
                    >
                        ×
                    </Button>
                </Box>
            )}

            <MapContainer
                key={`map-${tileStyle}-${isDarkMode}`}
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
            >
                <TileLayer
                    key={`tile-${tileStyle}-${isDarkMode}`}
                    {...tileConfig}
                    updateWhenZooming={false}
                    updateWhenIdle={true}
                    keepBuffer={2}
                />

                {/* Temporarily disabled MapBounds to prevent popup closing */}
                {/* <MapBounds schools={schoolsWithAddress} /> */}
                <MapClickHandler onMapClick={() => setOpenPopupSchool(null)} />

                {schoolsWithAddress.map((school, index) => {
                    // Validate school object
                    if (!school || !school.Id) {
                        console.warn('⚠️ SchoolMap: Invalid school object in map:', school);
                        return null;
                    }

                    // Get coordinates for this school
                    const coordinates = getSchoolCoordinates(school);

                    // If using fallback coordinates, add a small offset to prevent overlapping
                    const geocodingEntry = schoolsGeocoding.find(entry => entry.Id === school.Id);
                    const isUsingFallback = !geocodingEntry?.geocode;

                    const lat = isUsingFallback ? coordinates[0] + (index * 0.01) : coordinates[0];
                    const lng = isUsingFallback ? coordinates[1] + (index * 0.01) : coordinates[1];

                    return (
                        <Marker
                            key={`${school.Id}-${mapKey}`}
                            position={[lat, lng]}
                            eventHandlers={{
                                click: (e) => {
                                    debugLog('🗺️ Marker clicked for school:', school.DisplayName, school.Id);
                                    try {
                                        // More lenient check - just ensure we have a target
                                        if (e.target) {
                                            if (_onSchoolSelect) {
                                                _onSchoolSelect(school);
                                            }
                                            // Set the selected school for directions button
                                            setSelectedSchoolForDirections(school);
                                            // Set the popup to stay open for this school
                                            debugLog('🗺️ Setting openPopupSchool to:', school.DisplayName);
                                            setOpenPopupSchool(school);
                                        }
                                    } catch (error) {
                                        console.error('Error handling marker click:', error);
                                        // Still try to select the school even if there's an error
                                        if (_onSchoolSelect) {
                                            _onSchoolSelect(school);
                                        }
                                        setSelectedSchoolForDirections(school);
                                        setOpenPopupSchool(school);
                                    }
                                },
                            }}
                        >
                            {_showPopup && (
                                <Popup
                                    eventHandlers={{
                                        remove: () => {
                                            // Clear the open popup when it's closed
                                            setOpenPopupSchool(null);
                                        }
                                    }}
                                >
                                    <div dangerouslySetInnerHTML={{
                                        __html: createPopupContent(school, coordinates, isUsingFallback)
                                    }} />
                                </Popup>
                            )}
                        </Marker>
                    );
                })}
            </MapContainer>
        </Box>
    );
}; 